import { useTranslation } from 'react-i18next';
import { useMutation, UseMutationOptions, UseMutationResult } from '@tanstack/react-query';
import {
  hash,
  useNotifications,
  getResponseErrorMessage,
  usePerformanceReportBuilderPageStore,
} from '@diana/core';
import { updateLayoutRawJson } from '../Support';
import { PERFORMANCE_REPORT_BUILDER_PAGE_NS } from '../i18n';

type TVariables = {
  icon: string;
  color: string;
  imageUrl?: string;
};

export const useUpdateLayoutIconMutation = (
  options?: UseMutationOptions<void, unknown, TVariables>,
): UseMutationResult<void, unknown, TVariables> => {
  const { t } = useTranslation(PERFORMANCE_REPORT_BUILDER_PAGE_NS);

  const notifications = useNotifications();

  const getLayout = usePerformanceReportBuilderPageStore((s) => s.getLayout);
  const setLayout = usePerformanceReportBuilderPageStore((s) => s.setLayout);
  const registerRequest = usePerformanceReportBuilderPageStore((s) => s.registerRequest);

  return useMutation({
    mutationFn: async (value) => {
      const layout = getLayout();
      if (!layout) throw new Error('layout not found');

      setLayout({ ...layout, iconData: value });
      return await registerRequest(
        () => updateLayoutRawJson(layout.id, (content) => ({ ...content, iconData: value })),
        {
          key: hash(['update', 'layout', 'iconData', layout.id]),
          resourceId: layout.id,
          resourceType: 'layout',
        },
      );
    },
    ...options,
    onError: (error, variables, context) => {
      notifications.error({
        message: t('labels.savingError'),
        description: getResponseErrorMessage(error),
      });
      options?.onError?.(error, variables, context);
    },
  });
};
